import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "services/api";
import { User } from "types/user.types";

interface UserDetailsResponse {
  success: boolean;
  user: User;
}

export interface PatchUserRequest {
  firstname?: string;
  lastname?: string;
  email?: string;
  city?: string;
  country?: string;
  language?: string;
  allowsnotifications?: boolean;
  marketingmaterialok?: boolean;
  updatesok?: boolean;
  /** Admin-assignable app privilege (SUPER is rejected by the API). */
  privledgelevel?: "USER" | "ADMIN";
}

interface SimpleResponse {
  success: boolean;
  message: string;
}

interface LinkDeviceResponse {
  success: boolean;
  message: string;
  userId?: number;
  deviceId?: number;
  serialNumber?: string;
}

/**
 * Hook to fetch a single user by ID
 */
export const useUserDetails = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["user", userId],
    enabled: !!userId,
    queryFn: async (): Promise<UserDetailsResponse> => {
      const response = await api.get<UserDetailsResponse>(
        `admin/user/${userId}`,
      );
      return response.data;
    },
  });
};

/**
 * Hook to patch (edit) user details
 */
export const usePatchUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: string;
      data: PatchUserRequest;
    }): Promise<UserDetailsResponse> => {
      const response = await api.patch<UserDetailsResponse>(
        `admin/user/${userId}`,
        data,
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user", variables.userId] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

/**
 * Hook to verify a user's account
 */
export const useVerifyUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      userId: string,
    ): Promise<SimpleResponse & { userId?: number }> => {
      const response = await api.post<SimpleResponse & { userId?: number }>(
        `admin/user/${userId}/verify`,
      );
      return response.data;
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
};

/**
 * Hook to link a device to the user
 */
export const useLinkDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      serialNumber,
    }: {
      userId: string;
      serialNumber: string;
    }): Promise<LinkDeviceResponse> => {
      const response = await api.post<LinkDeviceResponse>(
        `admin/user/${userId}/device`,
        { serialNumber },
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user", variables.userId] });
    },
  });
};

/**
 * Hook to unlink the device from the user
 */
export const useUnlinkDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      userId: string,
    ): Promise<SimpleResponse & { userId?: number }> => {
      const response = await api.delete<SimpleResponse & { userId?: number }>(
        `admin/user/${userId}/device`,
      );
      return response.data;
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
};

/**
 * Hook to change a user's password (admin sets new password directly)
 */
export const useChangeUserPassword = () => {
  return useMutation({
    mutationFn: async ({
      userId,
      newPassword,
    }: {
      userId: string;
      newPassword: string;
    }): Promise<SimpleResponse> => {
      const response = await api.post<SimpleResponse>(
        `admin/user/${userId}/change-password`,
        { newPassword },
      );
      return response.data;
    },
  });
};

/**
 * Hook to send a password reset email to the user
 */
export const useSendPasswordReset = () => {
  return useMutation({
    mutationFn: async (userId: string): Promise<SimpleResponse> => {
      const response = await api.post<SimpleResponse>(
        `admin/user/${userId}/send-password-reset`,
      );
      return response.data;
    },
  });
};

/**
 * Hook to enable a user's account
 */
export const useEnableUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string): Promise<SimpleResponse> => {
      const response = await api.post<SimpleResponse>(
        `admin/user/${userId}/enable`,
      );
      return response.data;
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
};

/**
 * Hook to disable a user's account
 */
export const useDisableUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string): Promise<SimpleResponse> => {
      const response = await api.post<SimpleResponse>(
        `admin/user/${userId}/disable`,
      );
      return response.data;
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
};
